import React from "react";

import { ListItemIcon } from "@material-ui/core";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { useDispatch, useSelector } from "react-redux";
import { openEditConsignmentModal, openModalLink } from "lib/navigation";
import { ConsignmentModalSection, ModalTypes } from "constants/navigation";
import { StyledCheckbox } from "components/Form/FormikControls";
import { Row } from "components/Layout";
import {
  getBusinessById,
  getFileById,
  getManualAdjustments,
  getVendorIdByConsignmentId,
} from "selectors";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import Button from "components/Button";
import { faExternalLink } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components/macro";
import { hasImageExtension } from "constants/attachments";
import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { faFileAlt } from "@fortawesome/pro-duotone-svg-icons";
import { IdType } from ".";

type ManualAdjustmentsById = Record<number, ManualAdjustment>;

interface AttachmentComponentProps {
  attachmentId: IdType;
  selectedAttachmentIds: Array<number>;
  onToggle: () => void;
}

const WrapperImage = styled.div`
  top: 0;
  height: 36px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AttachmentComponent = ({
  attachmentId,
  selectedAttachmentIds,
  onToggle,
}: AttachmentComponentProps): React.ReactElement => {
  const attachment: Attachment = useSelector(getFileById(attachmentId));
  const checked = selectedAttachmentIds.includes(attachment.id);

  const dispatch = useDispatch();
  const labelId = `checkbox-list-label-${attachment.id}`;

  const attachmentConsignmentVendorId = useSelector(
    getVendorIdByConsignmentId(attachment.consignment),
  );

  const attachmentConsignmentVendor = useSelector(
    getBusinessById(attachmentConsignmentVendorId),
  );

  const manualAdjustmentByIdLookup = useSelector(
    getManualAdjustments,
  ) as ManualAdjustmentsById;

  const attachmentManualAdjustment =
    manualAdjustmentByIdLookup[attachment.manual_adjustment ?? ""] || {};

  const openConsignmentModal = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.stopPropagation();
    dispatch(
      openEditConsignmentModal(
        attachment.consignment,
        ConsignmentModalSection.MEDIA,
      ),
    );
  };

  const openManualAdjustmentModal = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.stopPropagation();
    dispatch(
      openModalLink(ModalTypes.ManualAdjustmentForm, {
        id: attachment.manual_adjustment,
      }),
    );
  };

  const src = attachment.thumbnail_url || attachment.image_url || "";

  const srcIsImage = hasImageExtension(src);

  let attachmentSource = "";

  if (attachment.consignment) {
    attachmentSource = "Consignment";
  }

  const attachmentText = (
    <Row alignCenter>
      {[
        attachment.type,
        attachmentSource,
        attachmentConsignmentVendor?.name,
        attachmentManualAdjustment?.note,
      ]
        .filter(Boolean)
        .join(" ")}
    </Row>
  );

  return (
    <Row justifyBetween fullWidth>
      <ListItem
        key={attachment.id}
        dense
        disableGutters
        button
        onClick={onToggle}
      >
        <ListItemIcon>
          <StyledCheckbox
            edge="start"
            checked={checked}
            tabIndex={-1}
            disableRipple
            inputProps={{ "aria-labelledby": labelId }}
          />
        </ListItemIcon>

        <WrapperImage>
          {srcIsImage ? (
            <img
              alt="billingDocumentRelatedAttachment"
              height={36}
              width={36}
              src={src}
            />
          ) : (
            <FaIcon icon={faFileAlt} color="primary" />
          )}
        </WrapperImage>

        <ListItemText id={labelId} primary={attachmentText} />
      </ListItem>

      <Button
        type="button"
        onClick={
          attachment.manual_adjustment
            ? openManualAdjustmentModal
            : openConsignmentModal
        }
      >
        <ButtonIcon icon={faExternalLink} />
      </Button>
    </Row>
  );
};
